import React, { useRef, useEffect } from 'react';
import { ChatBubble } from './ChatBubble';
import { ActionButtons } from './ActionButtons';
import { ChatMessage, Mode, ModelId } from '../../types';
import { HostType } from '../../types/office';
import { ScrollArea } from '@/components/ui/scroll-area';

interface ChatAreaProps {
  chat: ChatMessage[];
  isStreaming: boolean;
  mode: Mode;
  hasOriginalEmail: boolean;
  host: HostType;
  item: Office.MessageRead | Office.MessageCompose | null;
  onSummarize: () => void;
  onReplyOptions: () => void;
  onActionItems: () => void;
  onGrammar: () => void;
  onReview: () => void;
  onProfessional: () => void;
  onNotes?: () => void;
  onRewrite?: () => void;
  onPivotTable?: () => void;
  isDisabled?: boolean;
  setIsInserting: (isInserting: boolean) => void;
  selectedModel: ModelId;
  /** Standalone browser mode (no Office host): hides host-only actions like Insert. */
  standalone?: boolean;
}

export const ChatArea: React.FC<ChatAreaProps> = ({
  chat,
  isStreaming,
  mode,
  hasOriginalEmail,
  host,
  item,
  onSummarize,
  onReplyOptions,
  onActionItems,
  onGrammar,
  onReview,
  onProfessional,
  onNotes,
  onRewrite,
  onPivotTable,
  isDisabled = false,
  setIsInserting,
  selectedModel,
  standalone = false
}) => {
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when chat updates
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat, isStreaming]);

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="p-3 space-y-2 flex-shrink-0">
        <ActionButtons
          mode={mode}
          hasOriginalEmail={hasOriginalEmail}
          isStreaming={isStreaming}
          host={host}
          onSummarize={onSummarize}
          onReplyOptions={onReplyOptions}
          onActionItems={onActionItems}
          onGrammar={onGrammar}
          onReview={onReview}
          onProfessional={onProfessional}
          onNotes={onNotes}
          onRewrite={onRewrite}
          onPivotTable={onPivotTable}
          isDisabled={isDisabled}
        />
      </div>

      <ScrollArea className="flex-1 px-3">
        <div className="space-y-2 pb-2">
          {chat.map((msg, idx) => (
            <ChatBubble
              key={idx}
              message={msg}
              isStreaming={isStreaming}
              item={item}
              isDisabled={isDisabled}
              setIsInserting={setIsInserting}
              model={selectedModel}
              standalone={standalone}
            />
          ))}

          {isStreaming && (
            <div className="text-sm text-muted-foreground italic py-2">
              AI is typing...
            </div>
          )}
          
          <div ref={chatEndRef} />
        </div>
      </ScrollArea>
    </div>
  );
};